import { GMXNavigation } from './primary-nav';
import { loadHtmlPartialScripts } from "../../../utils/loadHtmlPartialScripts";
import { swapAuthLinks } from './auth-link-swap';

export class GMXFlyout extends HTMLElement {
    parentNavItem: HTMLElement | null
    hamburgerMenuCloseBtn: HTMLButtonElement
    hamburgerMenuContainer: HTMLButtonElement
    flyoutCollapseBtn: HTMLButtonElement | null
    flyoutCloseBtn: HTMLButtonElement | null
    GMXNavigation: GMXNavigation
    preloaded: boolean

    private initialized = false;
    private pendingOpen = false;

    private parentNavItemClick = this._handleParentNavItemClick.bind(this);
    private parentNavItemKeydown = this._handleParentNavItemKeydown.bind(this);
    private flyoutCollapseBtnClick = this._handleFlyoutCollapseBtnClick.bind(this);
    private flyoutCloseBtnClick = this._handleFlyoutCloseBtnClick.bind(this);

    connectedCallback(): void {
        this.preloaded = this.dataset.preloaded === 'true';

        if (this.preloaded || this.dataset.contentPath) {
            if (this.preloaded) {
                this.initFlyout()
            } else {
                try {
                    fetch(this.dataset.contentPath, {priority: "low"})
                        .then((response) => {
                            if (response.ok) {
                                response.text()
                                    .then((html) => {
                                        this.innerHTML = html
                                        loadHtmlPartialScripts(this, 'low');

                                        this.initFlyout()
                                        swapAuthLinks()
                                    })
                            } else {
                                window.console.error(`ERROR:  Failed to load flyout. response: ${response.statusText}`)
                                this._insertErrorLoadingMarkup()
                            }
                        }).catch(error => {
                        window.console.error(`ERROR:  Failed to load flyout: ${error}`)
                        this._insertErrorLoadingMarkup()
                    })
                } catch (error) {
                    window.console.error(`ERROR:  Failed to load flyout: ${error}`)
                    this._insertErrorLoadingMarkup()
                }
            }
        } else {
            this.parentNavItem = this.parentElement?.querySelector<HTMLElement>('.nav-btn') ?? null;
            window.console.error(`ERROR:  No content path or content found for flyout for ` + this.parentNavItem?.innerHTML)
            this._insertErrorLoadingMarkup()
        }
    }

    private _insertErrorLoadingMarkup(): void {
        this.innerHTML = `
            <div class="flyout-top">
                <button class="nav-small-button flyout-collapse-btn" aria-label="close menu"></button>
                <button class="nav-small-button gmx-btn gmx-btn-secondary flyout-close-btn" aria-label="close flyout" data-icon-name="close"></button>
            </div>
            <div class="flyout-loading-error-wrapper">
                <p>Sorry, there was an error loading this content.</p>
                <button class="flyout-retry-button gmx-btn gmx-btn-primary">Retry</button>
            </div>
        `
        this.initFlyout()
        const retryButton = this.querySelector('.flyout-retry-button')
        retryButton.addEventListener('click', () => {
            this.connectedCallback()
        })
    }

    private initFlyout(): void {
        this.GMXNavigation = this.closest('gmx-navigation') as GMXNavigation;

        this.hamburgerMenuCloseBtn = this.GMXNavigation.querySelector('button.close-btn');
        this.hamburgerMenuContainer = this.GMXNavigation.querySelector('.hamburger-submenu-container');
        // Detach before re-binding so a re-init doesn't stack duplicate handlers.
        this.flyoutCloseBtn = this.querySelector<HTMLButtonElement>('.flyout-close-btn');
        if (this.flyoutCloseBtn) {
            this.flyoutCloseBtn.removeEventListener('click', this.flyoutCloseBtnClick);
            this.flyoutCloseBtn.addEventListener('click', this.flyoutCloseBtnClick);
        }
        this.flyoutCollapseBtn = this.querySelector<HTMLButtonElement>('.flyout-collapse-btn');
        if (this.flyoutCollapseBtn) {
            this.flyoutCollapseBtn.removeEventListener('click', this.flyoutCollapseBtnClick);
            this.flyoutCollapseBtn.addEventListener('click', this.flyoutCollapseBtnClick);
        }

        // The trigger is a sibling, so it survives a re-init; detach before rebinding.
        if (this.parentNavItem) {
            this.parentNavItem.removeEventListener('click', this.parentNavItemClick);
            this.parentNavItem.removeEventListener('keydown', this.parentNavItemKeydown);
        }
        this.parentNavItem = this.parentElement?.querySelector<HTMLElement>('.nav-btn') ?? null;
        if (this.parentNavItem) {
            this.parentNavItem.addEventListener('click', this.parentNavItemClick);
            this.parentNavItem.addEventListener('keydown', this.parentNavItemKeydown);
        }

        // A top-bar link can be clicked while this flyout is still fetching, so honour an
        // open requested before there was anything to open.
        this.initialized = true;
        if (this.pendingOpen) {
            this.pendingOpen = false;
            this.open();
        }
    }

    disconnectedCallback(): void {
        // Detach handlers so none survive a later reconnect, and drop any deferred
        // open so a fetch landing after removal can't reveal this flyout.
        this.pendingOpen = false;
        this.flyoutCloseBtn?.removeEventListener('click', this.flyoutCloseBtnClick);
        this.flyoutCollapseBtn?.removeEventListener('click', this.flyoutCollapseBtnClick);
        if (this.parentNavItem) {
            this.parentNavItem.removeEventListener('click', this.parentNavItemClick);
            this.parentNavItem.removeEventListener('keydown', this.parentNavItemKeydown);
        }
    }

    private _handleParentNavItemClick(e: MouseEvent): void {
        // The trigger is an anchor pointing at the parent page. Let modified clicks
        // (cmd/ctrl/shift) and non-primary buttons perform the anchor's native
        // behaviour so the parent page opens in a new tab; otherwise open the flyout.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
            return;
        }
        e.preventDefault();
        this.toggle();
    }

    private _handleParentNavItemKeydown(e: KeyboardEvent): void {
        // Enter fires a native click (handled above); intercept Space so it opens
        // the flyout instead of scrolling the page, matching the old button.
        if (e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            this.toggle();
        }
    }

    public toggle(): void {
        if (this.classList.contains('open')) {
            this.collapse();
        } else {
            this.open();
        }
    }

    /** Drops an open requested before init, so a late fetch can't reveal this flyout behind a closed menu. */
    public cancelPendingOpen(): void {
        this.pendingOpen = false;
    }

    public open(): void {
        if (!this.initialized) {
            this.pendingOpen = true;
            return;
        }
        this.GMXNavigation.setBackgroundHandlerInactive();
        this.GMXNavigation.querySelectorAll('gmx-nav-flyout, gmx-nav-link-panel').forEach((panel) => panel.classList.remove('open'));
        this.GMXNavigation.querySelectorAll('.nav-btn, .nav-top-link').forEach((item) => item.classList.remove('active'));
        //todo: timeout to match animation pacing before opening next flyout?
        this._triggers().forEach((trigger) => trigger.classList.add('active'));
        this.classList.remove('close');
        this.classList.add('open');
        this.hamburgerMenuCloseBtn.classList.add('flyout-open');
        // Focus into the flyout on open.
        this.flyoutCloseBtn?.focus();
    }

    public collapse(): void {
        this._triggers().forEach((trigger) => trigger.classList.remove('active'));
        this.classList.remove('open');
        this.classList.add('close');
        this.hamburgerMenuCloseBtn.classList.remove('flyout-open');
        this.GMXNavigation.setBackgroundHandlerActive();
    }

    /**
     * Everything that opens this flyout: the menu item beside it, plus the top-bar link
     * mirroring that item, if the author asked for one. Both carry `.active` together so
     * neither is left marked open once the other closes the flyout.
     */
    private _triggers(): HTMLElement[] {
        const triggers = this.parentNavItem ? [this.parentNavItem] : [];
        const navRef = this.dataset.navRef;
        if (navRef) {
            triggers.push(
                ...this.GMXNavigation.querySelectorAll<HTMLElement>(`.nav-top-link[data-nav-ref="${navRef}"]`)
            );
        }
        return triggers;
    }

    private _handleFlyoutCollapseBtnClick(): void {
        this.collapse();
        // Return focus to the trigger on collapse.
        this.parentNavItem?.focus();
    }

    private _handleFlyoutCloseBtnClick(): void {
        // Closing the menu collapses this flyout; collapsing it again here would leave the
        // background handler active over a menu that is no longer open.
        this.GMXNavigation.hamburgerMenuClose();
    }
}
if (!customElements.get('gmx-nav-flyout')) {
    customElements.define('gmx-nav-flyout', GMXFlyout)
}